import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import { supabase, supabaseConfigError } from '../config/supabase'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [userProfileError, setUserProfileError] = useState(null)
  const [loading, setLoading] = useState(true)
  const isMountedRef = useRef(true)
  const isInitializedRef = useRef(false)

  const buildFallbackProfile = (sessionUser) => {
    const metadata = sessionUser?.user_metadata || {}
    return {
      user_id: sessionUser?.id || null,
      email: sessionUser?.email || metadata.email || '',
      first_name: metadata.first_name || metadata.firstName || metadata.given_name || '',
      last_name: metadata.last_name || metadata.lastName || metadata.family_name || '',
      role: metadata.role || 'guest',
      parent_id: metadata.parent_id ?? metadata.parentId ?? null,
    }
  }

  const isRecoverableProfileError = (error) => {
    if (!error) return false
    const message = error.message?.toLowerCase() || ''
    return (
      error.code === 'PGRST116' || // No rows returned for single()
      error.code === '42P01' || // Table does not exist
      message.includes('does not exist') ||
      message.includes('results contain 0 rows')
    )
  }

  useEffect(() => {
    if (supabaseConfigError) {
      console.error('Supabase configuration missing. Skipping auth initialization.')
      if (isMountedRef.current) {
        setLoading(false)
      }
      return () => {
        isMountedRef.current = false
      }
    }

    // Get initial session (with overall timeout so we never hang indefinitely in production)
    const AUTH_INIT_TIMEOUT_MS = 10000
    let timeoutId = null

    const forceFinishInit = () => {
      if (!isMountedRef.current) return
      setLoading(false)
      isInitializedRef.current = true
    }

    const initializeAuth = async () => {
      timeoutId = setTimeout(forceFinishInit, AUTH_INIT_TIMEOUT_MS)

      try {
        const { data: { session } } = await supabase.auth.getSession()

        if (session?.user) {
          setUser(session.user)
          await fetchUserProfile(session.user)
        } else {
          setUser(null)
          setUserProfile(null)
          setUserProfileError(null)
        }
      } catch (error) {
        console.error('Error getting initial session:', error)
        setUser(null)
        setUserProfile(null)
        setUserProfileError(null)
      } finally {
        if (timeoutId) clearTimeout(timeoutId)
        if (isMountedRef.current) {
          setLoading(false)
          isInitializedRef.current = true
        }
      }
    }

    initializeAuth()

    // Listen for auth changes (but ignore INITIAL_SESSION since we handle that above)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Skip INITIAL_SESSION event to avoid race condition with getSession() above
      if (event === 'INITIAL_SESSION') {
        return
      }

      // Don't interfere with page refresh - only handle user-initiated auth events
      if (!isInitializedRef.current) {
        return
      }

      // Only show loading for user-initiated auth events (sign in/out)
      const shouldShowLoading = event === 'SIGNED_IN' || event === 'SIGNED_OUT'

      if (shouldShowLoading) {
        setLoading(true)
      }

      try {
        if (session?.user) {
          setUser(session.user)
          await fetchUserProfile(session.user)
        } else {
          setUser(null)
          setUserProfile(null)
          setUserProfileError(null)
        }
      } catch (error) {
        console.error('Error in auth state change handler:', error)
      } finally {
        if (isMountedRef.current && shouldShowLoading) {
          setLoading(false)
        }
      }
    })

    return () => {
      isMountedRef.current = false
      if (timeoutId) clearTimeout(timeoutId)
      subscription.unsubscribe()
    }
  }, [])

  const fetchUserProfile = async (sessionUser) => {
    if (!sessionUser) return

    try {
      if (supabaseConfigError) {
        throw supabaseConfigError
      }
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', sessionUser.id)
        .single()

      if (error) {
        if (isRecoverableProfileError(error)) {
          const fallbackProfile = buildFallbackProfile(sessionUser)
          console.warn('User profile missing; using fallback profile from session metadata.', error)
          if (!isMountedRef.current) return
          setUserProfile(fallbackProfile)
          setUserProfileError('We could not find your saved profile. Using your account details instead.')
          return
        }
        throw error
      }

      if (!isMountedRef.current) return
      setUserProfile(data)
      setUserProfileError(null)
    } catch (error) {
      console.error('Error fetching user profile:', error)
      const fallbackProfile = buildFallbackProfile(sessionUser)
      if (!isMountedRef.current) return
      setUserProfile(fallbackProfile)
      setUserProfileError('We had trouble loading your profile. Some information may be limited.')
    }
  }

  const signUp = async (email, password, userData) => {
    try {
      if (supabaseConfigError) {
        throw supabaseConfigError
      }
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: userData.firstName,
            last_name: userData.lastName,
            role: userData.role,
            parent_id: userData.parentId || null
          }
        }
      })

      if (error) throw error

      // Create user_profiles row so parent/teacher can use Add Child, curricula, etc.
      if (data?.user) {
        const { error: profileError } = await supabase.from('user_profiles').insert({
          user_id: data.user.id,
          email: data.user.email || email,
          first_name: userData.firstName || '',
          last_name: userData.lastName || '',
          role: userData.role || 'guest',
          parent_id: userData.parentId || null
        })

        if (profileError) {
          console.error('Profile creation failed (profile may exist or RLS may block):', profileError)
          // Don't throw—auth succeeded; profile may be created by trigger or on first login
        }
      }

      return { data, error: null }
    } catch (error) {
      return { data: null, error }
    }
  }

  const signIn = async (email, password) => {
    try {
      if (supabaseConfigError) {
        throw supabaseConfigError
      }
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  }

  const signOut = async () => {
    if (supabaseConfigError) {
      return { error: supabaseConfigError }
    }
    const { error } = await supabase.auth.signOut()
    return { error }
  }

  const value = {
    user,
    userProfile,
    userProfileError,
    loading,
    signUp,
    signIn,
    signOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
